import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';

export default function FuturesPaperClosingRecovery({ positionId, onApplied }: { positionId: number; onApplied: () => Promise<void> }) {
    const action = useMutation(trpc.admin.reconcileFuturesPaperExternalClose.mutationOptions({ retry: false }));
    const [providerOrderId, setProviderOrderId] = useState('');
    const [reason, setReason] = useState('');
    const [review, setReview] = useState<{ receiptKey: string; fillPrice: number; fees: number; filledAt: number } | null>(null);
    const [message, setMessage] = useState('');
    const inspect = async (apply: boolean) => {
        try {
            const result = await action.mutateAsync({
                positionId,
                providerOrderId,
                reason,
                apply,
                ...(apply && review ? { expectedReceiptKey: review.receiptKey } : {})
            });
            setMessage(
                apply
                    ? 'Broker fill imported. Normal cash and flat-account reconciliation remain authoritative.'
                    : 'Exact terminal broker close verified. Review its actual fill and fees before importing.'
            );
            setReview(apply ? null : result);
            if (apply) await onApplied();
        } catch (error) {
            setMessage(error instanceof Error ? error.message : String(error));
            setReview(null);
        }
    };
    return (
        <section className="space-y-3 rounded border border-amber-700 p-4">
            <h3 className="font-semibold">Import a close made at the broker</h3>
            <p>
                This records an existing filled MES SIM close. The dedicated broker account must be flat with no active or uncertain orders.
                It never places an order.
            </p>
            <label className="block">
                Exact broker close order ID
                <input
                    className="ml-3 rounded bg-slate-900 p-2"
                    value={providerOrderId}
                    onChange={(e) => {
                        setProviderOrderId(e.target.value);
                        setReview(null);
                    }}
                />
            </label>
            <label className="block">
                Review notes
                <textarea
                    className="mt-1 block w-full rounded bg-slate-900 p-2"
                    value={reason}
                    minLength={20}
                    maxLength={1000}
                    onChange={(e) => {
                        setReason(e.target.value);
                        setReview(null);
                    }}
                />
            </label>
            <button
                className="rounded bg-blue-800 p-2 disabled:opacity-40"
                disabled={action.isPending || !providerOrderId || reason.trim().length < 20}
                onClick={() => void inspect(false)}
            >
                Preview existing close
            </button>
            {review && (
                <div className="space-y-2">
                    <p>
                        One contract at {review.fillPrice}; actual fees ${review.fees.toFixed(2)}; filled{' '}
                        {new Date(review.filledAt * 1000).toLocaleString()}.
                    </p>
                    <button
                        className="rounded bg-amber-800 p-2 disabled:opacity-40"
                        disabled={action.isPending}
                        onClick={() => void inspect(true)}
                    >
                        Import this reviewed broker fill
                    </button>
                </div>
            )}
            {message && <p role="status">{message}</p>}
        </section>
    );
}
